import "./insWorkspaceActionBar.scss";
import { push } from "connected-react-router";
import { isEqual } from "lodash";
import React from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "redux-undo";
import { getEditModeActionBarListButtonsData } from "./actionbar/insEditModeActionBar.helper";
import { useAllWorkspacesSelector } from "./hooks";
import { InsResponsiveListButton } from "./insResponsiveListButton";
import { useUpdateAnalysisMutation } from "./store/api";
import { setDynamicText } from "./store/workspaces";
import { MessagesDialogFunctionName } from "components/frame/helper";
import { useCurrentDashboardType, useDashboardFullPathInformations } from "components/workspaces/hooks/use-current-dashboard";
import { Breakpoints } from "hooks/use-breakpoint";
import { appStore } from "index";
import { appInsightEvents, language } from "language/default";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
import { setMessagesDialog } from "store/messages-dialog";
import { closeDesignerMode } from "store/template/designer";
import { selectedSectionIndexSelector, useEditedAnalysisIndex, useEditedAnalysisName, useEditedSections } from "store/template/designer/selectors";
import { getCurrentLocalTime } from "common/date-utils";
const isDefaultAnalysis = (template, editedAnalysisIndex) => {
    if (template.analysis && (editedAnalysisIndex || editedAnalysisIndex === 0)) {
        if (template.analysis[editedAnalysisIndex]?.isDefault) {
            return true;
        }
    }
    return false;
};
export const sectionOfWidgetFinder = (sectionList) => (widgetId) => {
    if (!widgetId) {
        return "";
    }
    const pageIdOfWidget = Object.keys(sectionList).find((sectionId) => {
        const section = sectionList[sectionId];
        return [
            ...section?.widgets?.all ?? [],
            ...section?.widgets?.wholeFundOnly ?? [],
            ...section?.widgets?.myPositionsOnly ?? []
        ].some((widget) => widget.id === widgetId);
    });
    return pageIdOfWidget;
};
export const cancelEdit = (removedSections, selectedSection, firstPast, pathInformations, dispatch) => {
    batch(() => {
        dispatch(closeDesignerMode());
        if (removedSections.some((s) => s === selectedSection)) {
            const pastSectionId = firstPast?.analysis?.sections?.[0]?.id;
            pastSectionId
                ? dispatch(push(`${pathInformations.slice(0, -1).join("/")}/${pastSectionId}`))
                : dispatch(push(`${pathInformations.slice(0, -1).join("/")}`));
        }
        dispatch(setDynamicText(language.en.dashboard.loadingAnalysis));
        dispatch(ActionCreators.clearHistory());
    });
};
export const InsWorkspaceEditModeActionBar = () => {
    const addedOrUpdatedWidgets = useSelector((state) => state.designer.present.addedOrUpdatedWidgets), currentAnalysisId = useSelector((state) => state.currentDashboard.currentAnalysisId), currentWorkspace = useAllWorkspacesSelector((w) => w.currentWorkspace), dispatch = useDispatch(), editedAnalysisIndex = useEditedAnalysisIndex(), editedAnalysisName = useEditedAnalysisName(), editedSections = useEditedSections(), firstPast = useSelector((state) => state.designer.past[0]), hasFuture = useSelector((state) => state.designer.future.length > 0), hasPast = useSelector((state) => state.designer.past.length > 0), removedSections = useSelector((state) => state.designer.present.removedSections) || [], removedWidgets = useSelector((state) => state.designer.present.removedWidgets) || {}, sections = useSelector((state) => state.designer.present.analysis?.sections, isEqual) || [], selectedType = useCurrentDashboardType(), template = useSelector((state) => state.entities.Template[selectedType], isEqual), widgets = useSelector((state) => state.designer.present.widgets), { pathInformations, selectedSection } = useDashboardFullPathInformations(), [updateAnalysis] = useUpdateAnalysisMutation();
    const selectedSectionIndex = useSelector((state) => selectedSectionIndexSelector(state, selectedSection));
    const sectionsFromDesign = useSelector((state) => state.designer.present.sections, isEqual) || {};
    const findSectionOfWidget = sectionOfWidgetFinder(sectionsFromDesign);
    const onSave = () => {
        appInsightsTrackEvent({
            name: appInsightEvents.customEvents.names.SaveAnalysis,
            properties: { LocalTimestamp: getCurrentLocalTime() }
        }, {
            analysisName: editedAnalysisName
        });
        if (isDefaultAnalysis(template, editedAnalysisIndex)) {
            return;
        }
        batch(() => {
            const widgetsMap = Object.values(widgets || [])
                .map((widget) => ({ ...widget, isDeleted: false, pageId: addedOrUpdatedWidgets?.[widget.id || ""]?.sectionId ?? findSectionOfWidget(widget.id || "") })) || [];
            const request = {
                id: currentAnalysisId,
                name: editedAnalysisName,
                sections: sections.filter((section) => !isEqual(section.id, removedSections?.find((s) => s === section.id)))
                    .map((section, index) => ({
                    ...editedSections?.[section.id],
                    id: section.id,
                    isDeleted: false,
                    name: section.name,
                    orderNumber: index
                }))
                    .concat(removedSections.map((section) => ({ id: section, isDeleted: true }))),
                widgets: Object.values(widgetsMap)
                    .concat(Object.keys(removedWidgets)
                    .filter((key) => !findSectionOfWidget(key))
                    .map((key) => ({ id: key, isDeleted: true, pageId: removedWidgets[key].sectionId }))),
                workspaceId: currentWorkspace
            };
            updateAnalysis(request);
            dispatch(ActionCreators.clearHistory());
        });
    };
    const navigateSectionAfterUndoRedo = () => {
        const sectionIdAfterUndoRedo = appStore?.getState()?.designer?.present?.analysis?.sections?.[selectedSectionIndex]?.id;
        if (sectionIdAfterUndoRedo === selectedSection) {
            return;
        }
        let currentSectionPath = "";
        if (sectionIdAfterUndoRedo === undefined) {
            const defaultSection = appStore?.getState()?.designer?.present?.analysis?.sections?.[0]?.id;
            currentSectionPath = `${pathInformations.slice(0, -1).join("/")}/${defaultSection}`;
        }
        else {
            currentSectionPath = `${pathInformations.slice(0, -1).join("/")}/${sectionIdAfterUndoRedo}`;
        }
        dispatch(push(currentSectionPath));
    };
    const onUndo = () => batch(() => {
        dispatch(ActionCreators.undo());
        navigateSectionAfterUndoRedo();
    });
    const onRedo = () => batch(() => {
        dispatch(ActionCreators.redo());
        navigateSectionAfterUndoRedo();
    });
    const onCloseEditor = () => batch(() => {
        if (hasPast) {
            dispatch(setMessagesDialog({ functionName: MessagesDialogFunctionName.CancelEdit,
                hasCustomFooter: false,
                messages: [{ message: language.en.dialogs.unsavedChanges.content }],
                title: language.en.dialogs.unsavedChanges.title,
                type: "prompt" }));
        }
        else {
            cancelEdit(removedSections, selectedSection, firstPast, pathInformations, dispatch);
        }
    });
    const editMenu = getEditModeActionBarListButtonsData({
        hasFuture,
        hasPast,
        onCloseEditor,
        onRedo,
        onSave,
        onUndo
    });
    return React.createElement("div", { className: "dashboardHeaderRight dashboardHeaderRightNoRightPadding" },
        React.createElement(InsResponsiveListButton, { limitBreakpoint: Breakpoints.xs, data: editMenu }));
};
